import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { RatingGrade } from '@tww/shared';
import { fmtDate } from '@tww/shared';
import { Icon } from './Icon';

/** StatusPill: Farbe aus action_status, Text aus Label */
export function StatusPill({ code, label }: { code: string; label?: string }) {
  return <span className={`pill ${code}`}>{label ?? code}</span>;
}

/** RatingBadge: A/B/C, Größe sm/lg, Tooltip mit Begründung und Datum */
export function RatingBadge({ grade, size = 'sm', reason, at }: { grade: RatingGrade | null | undefined; size?: 'sm' | 'lg'; reason?: string | null; at?: string | null }) {
  const title = grade ? [reason, at ? fmtDate(at) : null].filter(Boolean).join(' · ') : 'noch nicht bewertet';
  return (
    <span className={`rate ${grade ?? 'none'} ${size === 'lg' ? 'lg' : ''}`} title={title} aria-label={`Bewertung ${grade ?? 'offen'}`}>
      {grade ?? '–'}
    </span>
  );
}

/** Kennzahlen als Zeile: Zahl groß, Bezeichnung und Detail daneben; rot nur bei Handlungsbedarf */
export function KpiLine({ items }: { items: { label: string; value: ReactNode; detail?: ReactNode; variant?: 'ok' | 'alert' | 'water' | 'plain'; href?: string }[] }) {
  return (
    <div className="kpi-line">
      {items.map((k) => {
        const body = (
          <>
            <span className="v">{k.value}</span>
            <span className="l">{k.label}</span>
            {k.detail && <span className="d">{k.detail}</span>}
          </>
        );
        const cls = `kpi ${k.variant === 'alert' ? 'alert' : ''}`;
        return k.href ? (
          <Link key={k.label} className={cls} to={k.href}>
            {body}
          </Link>
        ) : (
          <div key={k.label} className={cls}>
            {body}
          </div>
        );
      })}
    </div>
  );
}

export function FactCard({ label, value, detail, missing, href }: { label: string; value: ReactNode; detail?: ReactNode; missing?: boolean; href?: string }) {
  return (
    <div className="fact">
      <div className="l">{label}</div>
      <div className={`v ${missing ? 'miss' : ''}`}>{href ? <Link className="link" to={href}>{value}</Link> : value}</div>
      {detail && <div className="d">{detail}</div>}
    </div>
  );
}
export function Facts({ children }: { children: ReactNode }) {
  return <div className="facts">{children}</div>;
}

/** ProgressStrip: Segmente je Gewerk */
export function ProgressStrip({ segments, pct }: { segments: { done: number; total: number; late?: boolean; name?: string }[]; pct?: number }) {
  return (
    <div className="strip" aria-label={`Fortschritt ${pct ?? ''}%`}>
      {segments.map((s, i) => {
        const p = s.total ? Math.round((100 * s.done) / s.total) : 0;
        return (
          <span key={i} className={`seg ${s.late ? 'late' : ''} ${p === 100 ? 'full' : ''}`} title={`${s.name ?? ''} ${s.done}/${s.total}`}>
            <i style={{ width: `${p}%` }} />
          </span>
        );
      })}
      {pct != null && <span className="pct">{pct} %</span>}
    </div>
  );
}

export function Panel({ title, action, children, className = '' }: { title?: ReactNode; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={`panel ${className}`}>
      {(title || action) && (
        <div className="ph">
          <h2>{title}</h2>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

export function Tabs({ items, active, onChange }: { items: { key: string; label: string; count?: ReactNode; warn?: boolean }[]; active: string; onChange: (k: string) => void }) {
  return (
    <div className="tabs" role="tablist">
      {items.map((t) => (
        <button key={t.key} role="tab" aria-selected={t.key === active} className={`tab ${t.key === active ? 'active' : ''}`} onClick={() => onChange(t.key)} type="button">
          {t.label}
          {t.count != null && <span className={`n ${t.warn ? 'miss' : ''}`}>{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}
export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const msg = error instanceof Error ? error.message : 'Unbekannter Fehler';
  return (
    <div className="error-state" role="alert">
      <Icon name="alert" />
      <div>{msg}</div>
      {retry && (
        <button className="btn sm" onClick={retry} type="button">
          <Icon name="refresh" /> Erneut versuchen
        </button>
      )}
    </div>
  );
}
export function Skeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="pb" style={{ display: 'flex', flexDirection: 'column', gap: 12 }} aria-busy="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton" style={{ width: `${60 + ((i * 17) % 40)}%` }} />
      ))}
    </div>
  );
}
export function Note({ kind, children }: { kind?: 'info' | 'good' | 'bad' | 'warn'; children: ReactNode }) {
  return <div className={`note ${kind && kind !== 'warn' ? kind : ''}`}>{children}</div>;
}
